import { useLocation, useNavigate } from "react-router-dom"
import { Data2, DataTypeList3, DirectArchiveModal, Labels, ListLoaderWrapper } from "../../../../../features/data-types"
import { Action, Badge, Column, FieldType, FieldValue, FilterField, FilterField2, FiltersOptions, ModalId, option, PanelWrapper, StaticValue, UIPermission, useUIActions } from "../../../../../features/ui"
import { DataType, TableName } from "../../../../../tables"
import { useManagerHooks } from "../../../../manager"
import { PaymentStatus } from "../../../../operations"
import { useSystemStateHooks } from "../../../../system"
import { HoardOrder, HoardType } from "../../../state/types"
import { useState } from "react"
import { useHoardLoaders } from "../../../loaders/hoardLoaders"
import { hoardHelpers, useHoardHooks } from "../../../hooks/hoardHooks"
import { HoardStatusFilter } from "../../../hooks/types"
import { DownloadSaleFilesModal } from "../../sale-operation/download-sale-files-modal/downloadSaleFilesModal"
import { SellerField } from "../../seller-field/sellerField"
import { CustomerField } from "../../customer/customer-field/customerField"
import { saleOperationHelpers, useSaleOperationHooks } from "../../../hooks/saleOperationHooks"
import { RoutePath } from "../../../../../features/routes"
import { FiscalDocumentStatus } from "../../../../fiscal-documents"
import { DeliveryStatus } from "../../../../deliveries"
import { BookmarkButton } from "../../../../../features/bookmarks"

export const HoardList = () => {
    const [currentHoard, setCurrentHoard] = useState<HoardOrder>()

    const { isInvoiceable } = hoardHelpers
    const { isArchivable } = saleOperationHelpers
    const { userHasPermission } = useSystemStateHooks()
    const { currentBranchId } = useManagerHooks()
    const { loadHoardsBatch, loadBatchData } = useHoardHooks()
    const { isEditableSync, isPayable, isDeliveryOrderEmitable } = useSaleOperationHooks()
    const navigate = useNavigate()
    const { pathname } = useLocation()

    const hoardLoaders = useHoardLoaders()

    const { openModal } = useUIActions()

    const readOnly = !userHasPermission(UIPermission.SAVE_HOARDS)
    const hasDeliveryPermission = userHasPermission(UIPermission.SAVE_SALE_DELIVERIES)
    const hasInvoicePermission = userHasPermission(UIPermission.INVOICE_HOARDS)

    const filterFields: FilterField2[] = [
        {
            name: 'deliveryStatus',
            type: FieldType.SELECT,
            options: Object.values(DeliveryStatus).map(status => option(status, Labels.deliveryStatusLabel(status))),
            label: 'Estado de Entrega',
            space: 2
        },
        {
            name: 'paymentStatus',
            type: FieldType.SELECT,
            options: Object.values(PaymentStatus).map(status => option(status, Labels.paymentStatusLabel(status))),
            label: 'Estado de Pago',
            space: 2
        },
        {
            ...SellerField({
                active: true
            }),
            space: 3
        } as FilterField,
        {
            ...CustomerField({ showAddress: true, active: true }),
            space: 3
        } as FilterField,
        {
            name: 'dateFrom',
            type: FieldType.DATE,
            label: 'Vencimiento: Desde',
            space: 2
        },
        {
            name: 'dateTo',
            type: FieldType.DATE,
            label: 'Vencimiento: Hasta',
            space: 2
        },
        {
            name: 'type',
            type: FieldType.SELECT,
            options: Object.values(HoardType).map(type => option(type, Labels.hoardTypeLabel(type))),
            label: 'Tipo',
            space: 3
        },
        {
            name: 'hoardStatus',
            type: FieldType.SELECT,
            options: [
                option(HoardStatusFilter.ACTIVE, 'Activos'),
                option(HoardStatusFilter.CLOSED, 'Cerrados')
            ],
            label: 'Estado del Acopio',
            space: 3
        }
    ]

    const filtersOptions: FiltersOptions = {
        filtersKey: 'hoards',
        search: {
            placeholder: 'Buscar por código'
        },
        filterFields,
        // Por defecto solo los acopios activos (no cerrados): un acopio puede seguir vigente meses después de creado
        defaultFiltersData: { hoardStatus: HoardStatusFilter.ACTIVE },
        filtersTextAffix: currentBranchId
    }

    const columns: Column[] = [
        {
            name: 'code',
            type: FieldType.NUMBER,
            label: '#',
            render: (value?: FieldValue) => `#${value}`
        },
        {
            name: 'type',
            type: FieldType.TEXT,
            label: 'Tipo',
            sortValue: (value?: FieldValue) => Labels.hoardTypeLabel(value as HoardType, true),
            render: (value?: FieldValue) => Labels.hoardTypeLabel(value! as HoardType, true)
        },
        {
            name: 'creationDate',
            type: FieldType.DATE,
            label: 'Fecha de Creación'
        },
        {
            name: 'dueDate',
            type: FieldType.DATE,
            label: 'Fecha Límite'
        },
        {
            name: 'value',
            type: FieldType.PRICE,
            label: 'Acopio Total',
            render: (value?: FieldValue) => value ?
                <StaticValue type={FieldType.PRICE} value={value} /> :
                <Badge label="Ver en detalle" />
        },
        {
            name: 'sellerId',
            type: FieldType.TABLE,
            table: TableName.EMPLOYEES,
            label: 'Vendedor'
        },
        {
            name: 'customerId',
            type: FieldType.TABLE,
            table: TableName.CUSTOMERS,
            label: 'Cliente'
        },
        {
            name: 'deliveryStatus',
            type: FieldType.TEXT,
            label: 'Estado de Entrega',
            sortValue: (value?: FieldValue) => Labels.deliveryStatusLabel(value as DeliveryStatus),
            render: (value?: FieldValue) => Labels.deliveryStatusLabel(value! as DeliveryStatus)
        },
        {
            name: 'paymentStatus',
            type: FieldType.TEXT,
            label: 'Estado de Pago',
            sortValue: (value?: FieldValue) => Labels.paymentStatusLabel(value as PaymentStatus),
            render: (value?: FieldValue) => Labels.paymentStatusLabel(value! as PaymentStatus)
        },
        {
            name: 'invoiceStatus',
            type: FieldType.TEXT,
            label: 'Facturación',
            sortValue: (value?: FieldValue) => Labels.fiscalDocumentStatusLabel(value as FiscalDocumentStatus),
            render: (value?: FieldValue) => Labels.fiscalDocumentStatusLabel(value! as FiscalDocumentStatus)
        }
    ]

    const toRow = (hoardDT: DataType): Data2 => {
        const { items, paymentItems, pricesList, ...data } = hoardDT as HoardOrder
        return data
    }

    const onDownloadFiles = (hoard: HoardOrder) => () =>{
        setCurrentHoard(hoard)
        openModal(ModalId.DOWNLOAD_FILES)
    }
    
    const onEmitDelivery = (id?: string) => navigate(`${id!}/${RoutePath.EMIT_DELIVERY}`)

    const onInvoice = (id?: string) => navigate(`${id!}/${RoutePath.INVOICE}`)

    const onArchive = (hoard: HoardOrder) => () => {
        setCurrentHoard(hoard)
        openModal(ModalId.DIRECT_ARCHIVE)
    }
    
    const actions = async (rowIdValue: string) => {
        const { loadHoard } = hoardLoaders()
        const hoard = await loadHoard(rowIdValue)

        const hoardHref = `${pathname}/${rowIdValue}`
        const actionList: Action[] = [
            { icon: 'eye-fill', kind: 'tertiary', tooltip: 'Ver Detalle', href: hoardHref },
            { icon: 'download', kind: 'tertiary', tooltipLines: ['Descargar', 'Documentos'], onClick: onDownloadFiles(hoard!) }
        ]

        if (!readOnly) {
            if (isEditableSync(hoard!)) {
                actionList.push({ icon: 'pencil-fill', tooltip: 'Editar', href: `${hoardHref}/${RoutePath.EDIT}` })
            }
            if (hasDeliveryPermission && isDeliveryOrderEmitable(hoard!)) {
                actionList.push({ icon: 'calendar-range', tooltip: 'Emitir Entrega', onClick: onEmitDelivery })
            }
            if (isPayable(hoard!)) {
                actionList.push({ icon: 'check-lg', tooltip: 'Confirmar Pago', href: `${hoardHref}/${RoutePath.CONFIRM_PAYMENT}` })
            }
        }
        if (hasInvoicePermission && isInvoiceable(hoard!)) {
            actionList.push({ icon: 'file-earmark-check', tooltip: 'Facturar', kind: 'quinary', onClick: onInvoice })
        }
        if (!readOnly && isArchivable(hoard!)) {
            actionList.push({ icon: 'archive-fill', kind: 'secondary', tooltip: 'Archivar Acopio', onClick: onArchive(hoard!) })
        }

        return actionList
    }

    return (
        <ListLoaderWrapper table={TableName.HOARD_ORDERS}>
            <>
                <PanelWrapper
                    title="Acopios"
                    wide
                    titleRight={<BookmarkButton routePath={RoutePath.HOARDS} />}
                >
                    <DataTypeList3
                        columns={columns}
                        filtersOptions={filtersOptions}
                        // Los acopios activos son un conjunto acotado (los cerrados se acumulan con los años)
                        canSort={filtersData => filtersData.hoardStatus === HoardStatusFilter.ACTIVE || !!filtersData.dateFrom || !!filtersData.customerId}
                        sortDisabledHint='Para ordenar, filtrá por acopios Activos, por fecha o por cliente'
                        readOnly={readOnly}
                        toRow={toRow}
                        actions={actions}
                        loadBatch={loadHoardsBatch}
                        loadBatchData={loadBatchData}
                    />
                </PanelWrapper>
                <DownloadSaleFilesModal
                    table={TableName.HOARD_ORDERS}
                    saleOperation={currentHoard}
                />
                <DirectArchiveModal
                    tableName={TableName.HOARD_ORDERS}
                    dataType={currentHoard}
                />
            </>
        </ListLoaderWrapper>
    )
}
